import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import { useParams, useHistory } from 'react-router-dom';
import { PrimaryButton, DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { fetchContract, fetchContractActivity, approve, reject } from '../../actions/contracts';
import { fetchAgreements } from '../../actions/agreements';
import TextButton from '../../components/TextButton';
import AgreementForm from '../../components/AgreementForm';
import Module from '../../components/Module';
import ModuleHeader from '../../components/ModuleHeader';
import ModuleContent from '../../components/ModuleContent';
import SubModule from '../../components/SubModule';
import SubModuleHeader from '../../components/SubModuleHeader';
import SubModuleContent from '../../components/SubModuleContent';
import PageTitle from '../../components/PageTitle';
import Main from '../../components/Main';
import SavingIndicator from '../../components/SavingIndicator';
import ContractActionRequired from '../../components/ContractActionRequired';
import styles from './ReviewContractAgreements.style';

/**
 * This page is only for approving a contract AND agreements after initial creation/onboarding!
 * If the contract isn't draft or pending initial approval, then you shouldn't be able to
 * access this page.
 */
const ReviewContractAgreements = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { contractAddress } = useParams();
  const agreementAddresses = useSelector(state =>
    get(state, `entities.contracts.${contractAddress}.agreements`, []),
  );
  const contract = useSelector(state => get(state, `entities.contracts.${contractAddress}`, {}));

  useEffect(() => {
    dispatch(fetchContractActivity(contractAddress));
    dispatch(fetchContract(contractAddress));
    dispatch(fetchAgreements(contractAddress));
  }, [dispatch, contractAddress]);

  // if the contract isn't draft or pending initial approval, then you shouldn't
  // be able to access this page
  useEffect(() => {
    if (
      contract.approvalStatus &&
      contract.approvalStatus !== 'PendingInitialApproval' &&
      !isSubmitting
    ) {
      history.push(`/contracts/${contractAddress}`);
    }
  }, [history, contract.approvalStatus, contractAddress, isSubmitting]);

  const handleCancel = async () => {
    history.push(`/contracts/${contractAddress}`);
  };

  const handleReject = async () => {
    setIsSubmitting(true);
    const response = await dispatch(reject(contractAddress));

    if (!response.error) {
      history.push(`/contracts/${contractAddress}`);
    } else {
      setIsSubmitting(false);
    }
  };

  const handleApprove = async () => {
    setIsSubmitting(true);
    const response = await dispatch(approve(contractAddress));

    if (!response.error) {
      history.push(`/contracts/${contractAddress}`);
    } else {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageTitle>Approve Contract</PageTitle>

      <Main>
        <ContractActionRequired
          address={contractAddress}
          hideCta
          className={styles.actionRequired}
        />

        <Module className={styles.agreementModule}>
          <ModuleHeader variant="xLarge">Royalty Fees</ModuleHeader>

          <ModuleContent>
            {agreementAddresses.map((address, index) => (
              <SubModule key={address} disableAccordian>
                <SubModuleHeader variant="large">{`Royalty Fee #${index + 1}`}</SubModuleHeader>
                <SubModuleContent>
                  <AgreementForm reviewMode contractAddress={contractAddress} address={address} />
                </SubModuleContent>
              </SubModule>
            ))}
          </ModuleContent>
        </Module>

        <div className={styles.buttonContainer}>
          <div className={styles.cancelButtonContainer}>
            <TextButton
              className={styles.cancelButton}
              onClick={handleCancel}
              disabled={isSubmitting}
              noPadding
            >
              Cancel
            </TextButton>
          </div>

          <div className={styles.saveSubmitButtonContainer}>
            {!isSubmitting ? (
              <>
                <DefaultButton onClick={handleReject}>Reject</DefaultButton>
                <PrimaryButton onClick={handleApprove} disabled={isSubmitting}>
                  Approve
                </PrimaryButton>
              </>
            ) : (
              <SavingIndicator className={styles.saving} />
            )}
          </div>
        </div>
      </Main>
    </>
  );
};

export default ReviewContractAgreements;
